import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useModal } from '@/contexts/modal'
import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getDepartmentsList, deleteDepartment } from '../services'
import { DEPARTMENT_STATUS, DEPARTMENT_EXCEL_COLUMNS } from '../constants'
import {
    getDepartmentListColumns,
    getDepartmentActions,
} from '../utils/department-list-config'

interface DepartmentFilters {
    department_status: string
}

export const useDepartmentList = () => {
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const { t } = useTranslation()

    // URL search params
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<DepartmentFilters>({
        defaults: { sort_by: 'department_id', sort_order: 'desc' },
        filterDefaults: {
            department_status: '',
        },
    })

    // Form for filter controls
    const { control } = useForm<DepartmentFilters>({
        defaultValues: filters,
    })

    // Fetch data
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS, queryParams],
        queryFn: () => getDepartmentsList(queryParams),
        placeholderData: true,
    })

    const departments = data?.results || []
    const pagination = data?.pagination

    // Delete mutation
    const { mutateAsync: doDeleteDepartment } = useMutationWithHandler(
        { mutationFn: deleteDepartment },
        {
            reactQueryKeys: QUERY_KEYS.DEPARTMENTS,
            successMessage: 'department_deleted_success',
            errorMessage: 'error_occurred',
            onSuccess: () => refetch(),
        }
    )

    // Memoized configs
    const columns = useMemo(() => getDepartmentListColumns(t), [t])

    const actions = useMemo(
        () =>
            getDepartmentActions({
                t,
                navigate,
                setConfirmation,
                setModal,
                doDeleteDepartment,
                refetch,
            }),
        [t, navigate, setConfirmation, setModal, doDeleteDepartment, refetch]
    )

    // Filter items config
    const filterItems = useMemo(
        () => [
            {
                name: 'department_status',
                type: 'select' as const,
                label: t('status'),
                placeholder: t('select_status'),
                options: DEPARTMENT_STATUS(t),
            },
        ],
        [t]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') => {
            setParams({ sort_by: sortBy, sort_order: sortOrder })
        },
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => {
            setParams({ page }, false)
        },
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => {
            setParams({ page_size: pageSize, page: 1 })
        },
        [setParams]
    )

    const stateComponent = renderState()

    return {
        // Data
        departments,
        pagination,
        stateComponent,
        // Form
        control,
        setFilter,
        // Configs
        columns,
        actions,
        filterItems,
        // Params
        params,
        queryParams,
        searchInputProps,
        // Excel export
        DEPARTMENT_EXCEL_COLUMNS,
        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        // Utils
        t,
    }
}
